import React, { useEffect, useState } from "react";
import {
  Check,
  Clock,
  CheckCircle2,
  Circle,
  AlertCircle,
  Loader2,
  ArrowLeftRight,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import { exchangeApi } from "../../api/exchangeApi";

export const ExchangeBanner = ({
  exchange,
  currentUserId,
  onConfirm,
  onDismiss,
  loading = false,
}) => {
  const [confirming, setConfirming] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!exchange) return;

    const isInitiator =
      currentUserId === exchange.initiator._id ||
      currentUserId === exchange.initiator;
    const isRecipient =
      currentUserId === exchange.recipient._id ||
      currentUserId === exchange.recipient;

    if (
      exchange.status === "completed" &&
      ((isInitiator && exchange.dismissed?.initiator) ||
        (isRecipient && exchange.dismissed?.recipient))
    ) {
      setDismissed(true);
    }
  }, [exchange, currentUserId]);

  if (!exchange || dismissed) return null;

  const isInitiator =
    currentUserId === exchange.initiator._id ||
    currentUserId === exchange.initiator;
  const isRecipient =
    currentUserId === exchange.recipient._id ||
    currentUserId === exchange.recipient;

  // Check if current user has confirmed
  const currentUserConfirmed = isInitiator
    ? exchange.initiatorConfirmed
    : exchange.recipientConfirmed;
  const otherUserConfirmed = isInitiator
    ? exchange.recipientConfirmed
    : exchange.initiatorConfirmed;

  const handleConfirm = async () => {
    if (currentUserConfirmed || confirming) return;

    setConfirming(true);
    try {
      await onConfirm(exchange._id);
    } catch (error) {
      console.error("Error confirming exchange:", error);
    } finally {
      setConfirming(false);
    }
  };

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await exchangeApi.dismissExchange(exchange._id);
      setDismissed(true);
      onDismiss?.(); // ✅ Inform parent to update its state
    } catch (error) {
      console.error("Error dismissing exchange:", error);
      setDismissing(false);
    } finally {
      setDismissing(false);
    }
  };

  const getStatusColor = () => {
    switch (exchange.status) {
      case "completed":
        return "bg-green-50 border-green-200";
      case "expired":
        return "bg-red-50 border-red-200";
      case "pending":
      default:
        return "bg-blue-50 border-blue-200";
    }
  };

  const getStatusIcon = () => {
    switch (exchange.status) {
      case "completed":
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case "expired":
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case "pending":
      default:
        return <Clock className="w-5 h-5 text-blue-600" />;
    }
  };

  const getStatusText = () => {
    switch (exchange.status) {
      case "completed":
        return "Exchange Completed!";
      case "expired":
        return "Exchange Expired";
      case "pending":
      default:
        return "Exchange Pending";
    }
  };

  return (
    <div
      className={`mx-4 mb-3 p-4 rounded-lg border-2 ${getStatusColor()} transition-all duration-200`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          {getStatusIcon()}
          <h3 className="font-semibold text-gray-900 text-sm">
            {getStatusText()}
          </h3>
        </div>
        {exchange.status === "pending" && (
          <div className="text-xs text-gray-500">
            Expires: {new Date(exchange.expiresAt).toLocaleDateString()}
          </div>
        )}
        {exchange.status === "completed" && (
          <Button
            onClick={handleDismiss}
            variant="ghost"
            size="icon"
            disabled={dismissing}
          >
            <X />
          </Button>
        )}
      </div>

      {/* Barter Info - Two-way exchange display */}
      <div className="mb-3">
        <div className="flex items-center justify-between space-x-2">
          {/* Initiator's Listing */}
          <div className="flex-1 text-center">
            <p className="text-xs text-gray-500 mb-1">
              {exchange.initiator?.firstName || "Initiator"} offers:
            </p>
            <p className="text-sm font-medium text-gray-700 bg-white px-2 py-1 rounded border">
              {exchange.initiatorListing?.title || "Unknown Listing"}
            </p>
          </div>

          {/* Exchange Arrow */}
          <div className="flex-shrink-0 px-2">
            <ArrowLeftRight className="w-5 h-5 text-gray-400" />
          </div>

          {/* Recipient's Listing */}
          <div className="flex-1 text-center">
            <p className="text-xs text-gray-500 mb-1">
              {exchange.recipient?.firstName || "Recipient"} offers:
            </p>
            <p className="text-sm font-medium text-gray-700 bg-white px-2 py-1 rounded border">
              {exchange.recipientListing?.title || "Unknown Listing"}
            </p>
          </div>
        </div>
      </div>

      {/* Confirmation Status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-4">
          {/* Initiator Status */}
          <div className="flex items-center space-x-2">
            {exchange.initiatorConfirmed ? (
              <CheckCircle2 className="w-4 h-4 text-green-500" />
            ) : (
              <Circle className="w-4 h-4 text-gray-400" />
            )}
            <span className="text-xs text-gray-600">
              {exchange.initiator?.firstName || "Initiator"}
              {isInitiator && " (You)"}
            </span>
          </div>

          {/* Recipient Status */}
          <div className="flex items-center space-x-2">
            {exchange.recipientConfirmed ? (
              <CheckCircle2 className="w-4 h-4 text-green-500" />
            ) : (
              <Circle className="w-4 h-4 text-gray-400" />
            )}
            <span className="text-xs text-gray-600">
              {exchange.recipient?.firstName || "Recipient"}
              {isRecipient && " (You)"}
            </span>
          </div>
        </div>
      </div>

      {/* Action Button */}
      {exchange.status === "pending" && !currentUserConfirmed && (
        <div className="flex justify-center">
          <Button
            onClick={handleConfirm}
            disabled={confirming || loading}
            size="sm"
            className="bg-igbanwo-mint hover:bg-emerald-600 text-white px-6"
          >
            {confirming ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Confirming...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Confirm Exchange
              </>
            )}
          </Button>
        </div>
      )}

      {/* Status Messages */}
      {exchange.status === "pending" && currentUserConfirmed && (
        <div className="text-center">
          <p className="text-sm text-green-600 font-medium">
            ✓ You've confirmed this exchange
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Waiting for the other party to confirm
          </p>
        </div>
      )}

      {exchange.status === "completed" && (
        <div className="text-center">
          <p className="text-sm text-green-600 font-medium">
            🎉 Exchange completed successfully!
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Completed on {new Date(exchange.completedAt).toLocaleDateString()}
          </p>
        </div>
      )}

      {exchange.status === "expired" && (
        <div className="text-center">
          <p className="text-sm text-red-600 font-medium">
            ⏰ This exchange has expired
          </p>
          <p className="text-xs text-gray-500 mt-1">
            You can create a new exchange if needed
          </p>
        </div>
      )}
    </div>
  );
};
